import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  formatRouteString,
  IsServiceTypeVendor,
  tokenDecoder,
} from "../lib/utils";
import { useSelector, useDispatch } from "react-redux";
import { setAppState } from "../redux/slices/appState";
import { HiOutlineX } from "react-icons/hi";
import SortupDialog from "./common/SortupDialog";
import { AWS_CLOUD_FRONT_URL } from "../lib/constants";

const innerDivStyle: React.CSSProperties = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  paddingTop: "0.5rem",
  paddingBottom: "0.5rem",
};

interface SidebarProps {
  showAlertOnRouteChange: boolean;
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
  isSidebarOpen: boolean;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAlertAction: React.Dispatch<React.SetStateAction<boolean>>;
}

const Sidebar: React.FC<SidebarProps> = ({
  showAlertOnRouteChange,
  setShowAlertOnRouteChange,
  isSidebarOpen,
  setIsSidebarOpen,
  setShowAlertAction,
}) => {
  const location = useLocation();
  const token = useSelector((state: any) => state.auth.token);
  const appState = useSelector((state: any) => state.appState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const decodedToken = tokenDecoder(token);
  const [routeName, setRouteName] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingRoute, setPendingRoute] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const pathname = location.pathname.substring(1);
    dispatch(setAppState({ routeName: formatRouteString(pathname) }));
  }, [location.pathname, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only handle clicks when sidebar is open and on mobile
      if (isSidebarOpen && isMobile) {
        const sidebarElement = document.querySelector(".sidebar-container");
        if (sidebarElement && !sidebarElement.contains(event.target as Node)) {
          setIsSidebarOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarOpen, isMobile, setIsSidebarOpen]);

  // Build navItems based on the user role
  let navItems = [];

  if (decodedToken.role === "ADMIN") {
    navItems = [
      { name: "dashboard", icon: "/Images/SidebarImages/Category.png" },
      { name: "vendors", icon: "/Images/SidebarImages/User.png" },
      { name: "configuration", icon: "/Images/SidebarImages/Shop.png" },
      { name: "customers", icon: "/Images/SidebarImages/User.png" },
      { name: "reviews", icon: "/Images/SidebarImages/Star.png" },
      { name: "orders", icon: "/Images/SidebarImages/Bag.png" },
      { name: "payouts", icon: "/Images/SidebarImages/Payout.png" },
      { name: "videos", icon: "/Images/SidebarImages/Video.png" },
      { name: "layout", icon: "/Images/SidebarImages/Layout.png" },
    ];
  } else if (decodedToken.role === "VENDOR") {
    if (IsServiceTypeVendor()) {
      navItems = [
        { name: "dashboard", icon: "/Images/SidebarImages/Category.png" },
        { name: "bookings", icon: "/Images/SidebarImages/PhoneCall.png" },
        { name: "GoLive", icon: "/Images/SidebarImages/GoLive.png" },
        { name: "store", icon: "/Images/SidebarImages/Shop.png" },
        { name: "services", icon: "/Images/SidebarImages/Box.png" },
        { name: "orders", icon: "/Images/SidebarImages/Bag.png" },
        { name: "reviews", icon: "/Images/SidebarImages/Star.png" },
        { name: "videos", icon: "/Images/SidebarImages/Video.png" },
        // Uncomment if you want to add Chats:
        // { name: "Chats", icon: "/Images/SidebarImages/Chats.png" },
      ];
    } else {
      navItems = [
        { name: "dashboard", icon: "/Images/SidebarImages/Category.png" },
        { name: "GoLive", icon: "/Images/SidebarImages/GoLive.png" },
        { name: "store", icon: "/Images/SidebarImages/Shop.png" },
        { name: "products", icon: "/Images/SidebarImages/Box.png" },
        { name: "orders", icon: "/Images/SidebarImages/Bag.png" },
        { name: "reviews", icon: "/Images/SidebarImages/Star.png" },
        { name: "videos", icon: "/Images/SidebarImages/Video.png" },
        // Uncomment if you want to add Chats:
        // { name: "Chats", icon: "/Images/SidebarImages/Chats.png" },
      ];
    }
  } else {
    // Fallback for other roles
    navItems = [
      { name: "dashboard", icon: "/Images/SidebarImages/Category.png" },
      { name: "orders", icon: "/Images/SidebarImages/Bag.png" },
    ];
  }

  const displayedNavItems = navItems;

  const handleLinkClick = (name: string) => {
    setPendingRoute(name);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
    if (showAlertOnRouteChange) {
      setIsDialogOpen(true);
    } else {
      navigate(`/${name}`);
      dispatch(setAppState({ routeName: name }));
      setIsSidebarOpen(false);
    }
  };

  const handleConfirmNavigation = () => {
    setShowAlertOnRouteChange(false);
    setIsDialogOpen(false);
    setShowAlertAction(true);
    setTimeout(() => {
      navigate(`/${pendingRoute}`);
      dispatch(setAppState({ routeName: pendingRoute }));
    }, 1000);
  };

  return (
    <>
      <SortupDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        header="Confirm Navigation"
        bodyClassName=""
        footerButtons={[
          {
            name: "Leave",
            onClick: handleConfirmNavigation,
            disabled: false,
            className: "bg-customGreenColor text-white",
          },
          {
            name: "Cancel",
            onClick: () => setIsDialogOpen(false),
            disabled: false,
            className: "bg-white text-gray-900 border hover:bg-gray-100",
          },
        ]}
      >
        <div className="px-4 py-2">
          <div className="text-sm text-gray-900">
            You have unsaved changes, are you sure want to leave?
          </div>
        </div>
      </SortupDialog>
      <div className="h-screen py-4 bg-customGreenColor flex flex-col justify-between overflow-hidden relative pb-20 overflow-y-auto sidebar-container">
        <div>
          <div className="flex items-center px-3 mb-8 sticky top-0 bg-customGreenColor">
            <Link to="/dashboard" target="_self" className="flex items-center">
              <img
                src={`${AWS_CLOUD_FRONT_URL}/AfroCart_Logo_White.png`}
                className="w-36"
                alt="logo"
              />
            </Link>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="md:hidden text-white"
            >
              <HiOutlineX className="w-6 h-6 ml-2" />
            </button>
          </div>
          <div className="space-y-4 mt-10 text-sm py-1">
            {displayedNavItems.map((item) => (
              <div
                key={item.name}
                style={innerDivStyle}
                className={`pl-4 pr-3 cursor-pointer ${
                  appState.routeName === item.name
                    ? "border-l-4 border-white rounded-r-lg opacity-100"
                    : "opacity-70 hover:text-white hover:opacity-100"
                }`}
                onClick={() => handleLinkClick(item.name)}
              >
                <img
                  src={item.icon}
                  alt={item.name}
                  className={`mr-4 font-bold ${
                    appState.routeName === item.name && "-ml-1"
                  }`}
                  style={{ width: 20, height: 20 }}
                />
                <span
                  className={`${
                    appState.routeName === item.name && "text-white"
                  } capitalize text-base`}
                >
                  {item.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
